export const DEFAULT_DELIVERY_DETAIL = {
  name: "",
  email: "",
  phone: "",
  address_1: "",
  address_2: "",
  city: "",
  state: "",
  zipCode: "",
  country: "",
};

export const parseUSAddress = (address) => {
  const result = {
    address_1: "",
    address_2: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
  };

  if (!address) return result;

  const parts = address.split(",").map((part) => part.trim());
  if (parts.length < 4) {
    result.address_1 = address; // If not enough parts, treat the whole address as address_1
    return result;
  }

  result.country = parts[parts.length - 1];
  // State and ZIP are expected to be in the second-to-last part
  const stateZipArray = (parts[parts.length - 2] || "").split(" ").filter((str) => str.trim() !== "");
  if (stateZipArray.length > 1) {
    result.zipCode = stateZipArray[stateZipArray.length - 1];
    result.state = stateZipArray.slice(0, stateZipArray.length - 1).join(" ");
  } else {
    result.state = stateZipArray[0] || "";
  }
  result.city = parts[parts.length - 3];

  // Determine address lines
  if (parts.length === 5) {
    result.address_1 = parts[0];
    result.address_2 = parts[1];
  } else if (parts.length === 4) {
    result.address_1 = parts[0];
    result.address_2 = "";
  } else {
  // If more than 6 parts, combine all parts before city as address lines
    result.address_1 = parts.slice(0, parts.length - 3).join(", ");
    result.address_2 = "";
  }

  return result;
};

export const parseCustomerAddressFromData = (data) => {
  const {
    address, name, contact_name, email, phone,
  } = data || {};
  return {
    name: name || contact_name || "",
    email: email || "",
    phone: phone || "",
    ...parseUSAddress(address),
  };
};

/**
 * Parse backend pickup_address entries:
 * [{ id, contact_name, email, phone, address }]
 * The id is retained so saved addresses keep a stable identity in the UI.
 */
export const parsePickupAddressEntry = (entry) => ({
  ...parseCustomerAddressFromData(entry),
  id: entry?.id,
});

export const parsePickupAddressList = (pickupAddressList) => {
  if (!Array.isArray(pickupAddressList)) return [];
  return pickupAddressList
    .filter(Boolean)
    .map(parsePickupAddressEntry);
};

/**
 * Parse backend supplies_address entries:
 * [{ id, contact_name, email, phone, address }]
 */
export const parseSuppliesAddressList = (suppliesAddressList) => (
  parsePickupAddressList(suppliesAddressList)
);

export const parseSuppliesAddressFromResponse = (apiData) => {
  const fromSuppliesList = parseSuppliesAddressList(apiData?.supplies_address);
  return fromSuppliesList[0] || DEFAULT_DELIVERY_DETAIL;
};

export const parseCustomerAddressFromResponse = (apiData) => {
  const fromPickupList = parsePickupAddressList(apiData?.pickup_address);
  return fromPickupList[0] || DEFAULT_DELIVERY_DETAIL;
};

export const getAddressId = (address, index = 0) => (
  address?.id
  || `${address?.name || "address"}-${address?.address_1 || ""}-${address?.zipCode || index}`
);

export const getAddressOptionLabel = (address) => {
  if (!address) return "-";
  const street = [address.address_1, address.city, address.state, address.zipCode]
    .filter(Boolean)
    .join(", ");
  if (address.name && street) return `${address.name} - ${street}`;
  return address.name || street || "-";
};

export const formatAddressLine = (address, emptyLabel = "") => {
  if (!address) return emptyLabel;
  const {
    address_1, address_2, city, state, zipCode, country,
  } = address;
  return [
    address_1,
    address_2,
    city,
    state && zipCode ? `${state} ${zipCode}` : state || zipCode,
    country,
  ].filter(Boolean).join(", ") || emptyLabel;
};

/** Required fields for supply delivery / pickup address details. Email is optional. */
export const isAddressDetailValid = (address) => {
  if (!address) return false;
  const {
    name, address_1, city, state, zipCode, country,
  } = address;
  return Boolean(name && address_1 && city && state && zipCode && country);
};
